<template>
  <div class="product-details-wrapper medium-8">
    <!-- Product Description -->
    <div v-if="details.description" class="product-details-section product-description-section">
      <h2 class="large-3">Product description</h2>
      <div class="large-9">
        <p v-html="unescapeHtml(details.description)"></p>
        <img src="https://www.atlasantibodies.com/image/86830/MolBoolean-Kit-1.jpg" alt="MolBoolean Kit Box" :class="{ 'mol-boolean-margin': !molBooleanKitText }">
        <div v-if="molBooleanKitText" class="molboolean-icon-container">
          <p>{{ molBooleanKitText }}</p>
        </div>
      </div>

    </div>
    <!-- <hr v-if="details.description"> -->
    <!-- End Product Description -->

    <!-- Product Details -->
    <div class="product-details-section">
      <h2 class="large-3">Product details</h2>
      <ul class="large-9">
        
        <!-- MolBoolean category details -->
        <div v-if="categoryId == 615">
          <li v-if="details.product_details?.included_in_kit">
            <span class="key-value">Included in the Kit:&nbsp;</span>
            <span>{{ details.product_details?.included_in_kit }}</span>
          </li>
          <li v-if="details.product_details?.not_included_in_kit">
            <span class="key-value">Not included in the Kit:&nbsp;</span>
            <span>{{ details.product_details?.not_included_in_kit }}</span>
          </li>
          <li v-if="details.product_details?.target_species">
            <span class="key-value">Target Species:&nbsp;</span>
            <span v-for="species, index in details.product_details?.target_species" :key="species">
              {{ species }}{{ index == details.product_details?.target_species.length -1 ? '.' : ', ' }}
            </span>
          </li>
          <li v-if="packaging?.kit_size">
            <div v-for="kit_size in packaging?.kit_size" :key="kit_size">
              <div v-for="(value, kit_size_name) in kit_size" :key="value">
                <span class="key-value">Kit Size:&nbsp;</span>
                <span>{{ kit_size_name }}&nbsp;{{ value }}</span>
              </div>
            </div>
          </li>
          <div v-if="details.product_details?.material_safety_data_sheet">
            <a :href="details.product_details.material_safety_data_sheet.url" target="_blank" class="link molvoolean-safety-sheet">
              {{ details.product_details.material_safety_data_sheet.name }}
            </a>
          </div>
        </div>
        <!-- End MolBoolean category details -->

        <li v-if="details.target?.protein_name">
          <span class="key-value">Target protein:&nbsp;</span>
          <span>{{ details.target?.protein_name }}</span>
        </li>
        <li v-if="details.target?.gene_name">
          <span class="key-value">Target gene:&nbsp;</span>
          <span>{{ details.target?.gene_name }}</span>
        </li>
        <li v-if="details.verified_species_reactivity">
          <span class="key-value">Verified species reactivity:&nbsp;</span>
          <span v-for="species, index in details.verified_species_reactivity" :key="species">
            {{ species }}{{ index == details.verified_species_reactivity.length -1 ? '' : ', ' }}
          </span>
        </li>
        <li v-if="details.interspecies_information">
          <span class="key-value">Interspecies information:&nbsp;</span>
          <span>Highest antigen sequence indentity to the following orthologs:&nbsp;</span>
          <span v-for="item in details.interspecies_information" :key="item.database_entry_id" class="block">
            <span>{{ item.species_id }}&nbsp;-&nbsp;</span>
            <span>{{ item.database_entry_id }}&nbsp;</span>
            <span>({{ item.identity_percent }})</span>
          </span>
        </li>
        <li v-if="details.fusion_tag">
          <span class="key-value">Fusion tag:&nbsp;</span>
          <span v-html="details.fusion_tag"></span>
        </li>
        <li v-if="details.expression_host">
          <span class="key-value">Expression host:&nbsp;</span>
          <span>{{ details.expression_host }}</span>
        </li>
        <li v-if="details.clonality">
          <span class="key-value">Clonality:&nbsp;</span>
          <span>{{ details.clonality }}</span>
        </li>
        <li v-if="details.clone_id">
          <span class="key-value">Clone ID:&nbsp;</span>
          <span>{{ details.clone_id }}</span>
        </li>

        <li v-if="details.isotype">
          <span class="key-value">Isotype:&nbsp;</span>
          <span>{{ details.isotype }}</span>
        </li>
        <li v-if="details.host_species">
          <span class="key-value">Host:&nbsp;</span>
          <span>{{ details.host_species }}</span>
        </li>
        <li v-if="details.buffer?.description">
          <span class="key-value">Buffer:&nbsp;</span>
          <span>{{ details.buffer.description }}</span>
          <br>
          <a :href="details.buffer.link.url" target="_blank" class="link">
            {{ details.buffer.link.name }}
          </a>
        </li>
        <li v-if="details.purification_method">
          <span class="key-value">Purification method:&nbsp;</span>
          <span>{{ details.purification_method }}</span>
        </li>
        <li v-if="details.purity">
          <span class="key-value">Purity:&nbsp;</span>
          <span>{{ details.purity }}</span>
        </li>
        <li v-if="details.theoretical_mw">
          <span class="key-value">Theoretical MW:&nbsp;</span>
          <span>{{ details.theoretical_mw }}</span>
        </li>
        <li v-if="details.concentration">
          <span class="key-value">Concentration:&nbsp;</span>
          <span>{{ details.concentration }}</span>
        </li>
        <li v-if="details.antigen_sequence">
          <span class="key-value">Antigen sequence:&nbsp;</span>
          <span>Recombinant Protein Epitope Signature Tag (PrEST) antigen sequence</span>
        </li>
        <li v-if="details.antigen_sequence">
          <div class="highlight-container">
            <span class="antigen-sequence">{{ details.antigen_sequence }}</span>
            <button @click="copySequenceToClipboard(details.antigen_sequence)">
              <span class="clipboard-icon">
                <ClipboardIcon/>
              </span>
              Copy sequence
            </button>
          </div>
        </li>
        <li v-if="details.amino_acid_sequence">
          <span class="key-value">Amino acid sequence:&nbsp;</span>
        </li>
        <li v-if="details.amino_acid_sequence">
          <div class="highlight-container">
            <span class="antigen-sequence">{{ details.amino_acid_sequence }}</span>
            <button @click="copySequenceToClipboard(details.amino_acid_sequence)">
              <span class="clipboard-icon">
                <ClipboardIcon/>
              </span>
              Copy sequence
            </button>
          </div>
        </li>
        <li v-if="details.notes">
          <span class="key-value margin-top-20 block">Notes:&nbsp;</span>
          <span class="block">{{ details.notes }}</span>
        </li>
        <li v-if="details.tags">
          <span class="key-value margin-top-20 block">Research area:&nbsp;</span>
          <div class="margin-top-10">
            <a 
              v-for="tag, key in tags"
              :href="tag.href"
              :key="key"
              target="_blank"
              class="button secondary research-area-tag"
            >
              {{ tag.name }}
            </a>
          </div>
        </li>

      </ul>
    </div>
    <!-- End Product Details -->

    <!-- User instruction -->
    <div v-if="details.product_details" class="product-details-section">
      <h2 class="large-3">User instruction</h2>
      <ul class="user-instruction large-9">

          <a :href="'/products-g157/molboolean-kit-info/'" target="_blank" class="link molvoolean-safety-sheet">
            Click on this link
          </a>
          <span>and find all the necessary instructions for the product use:</span>

        <li>
          <span>Instructions for use</span>
        </li>
        <li>
          <span>Tools for image analysis</span>
        </li>
        <li>
          <span>Video Image analysis support</span>
        </li>
        <li>
          <span>FAQ</span>
        </li>
      </ul>
    </div>
    <!-- End User instruction -->
    
    <!-- Current lot -->
    <!-- <hr v-if="packaging.current_lot"> -->
    <div v-if="currentLotSimplified" class="product-details-section">
      <h2 class="large-3">Current lot</h2>
      <ul class="large-9">
        <li v-if="currentLotSimplified?.volume">
          <span class="key-value">Unit size:&nbsp;</span>
          <span>{{ currentLotSimplified?.volume }}</span>
        </li>
        <li v-if="currentLotSimplified?.current_batch">
          <span class="key-value">Current lot:&nbsp;</span>
          <span>{{ currentLotSimplified?.current_batch }}</span>
        </li>
        <li v-if="currentLotSimplified?.concentration">
          <span class="key-value">Concentration:&nbsp;</span>
          <span>{{ currentLotSimplified?.concentration }}</span>
        </li>
      </ul>
    </div>
    <!-- End Current lot -->
    
    <!-- Applications -->
    <!-- <hr v-if="Array.isArray(details.applications)"> -->
    <div v-if="Array.isArray(details.applications)" class="product-details-section applications">
      <h2 class="large-3">Applications</h2>
      <ul v-if="Array.isArray(details.applications)" class="large-9">

        <li 
          v-for="application, applicationKey in details.applications"
          :key="applicationKey"
          class="margin-top-20"
        >
          <div>
            <span v-if="application.name" class="key-value">{{ application.name }}</span>
            <span v-if="application.type" class="key-value">&nbsp;({{ application.type }})</span>
            <span class="key-value">&nbsp;recommended conditions</span>
          </div>

          <ul>
            <li v-for="condition, conditionKey in application.recommended_conditions" :key="conditionKey">
              <span class="key-value">{{ conditionKey }}:&nbsp;</span>
              <span class="">{{ condition }}</span>
            </li>
          </ul>
          
        </li>
        <li>
          <div class="margin-top-20">
            <a
              v-if="details.application_protocols"
              :href="details.application_protocols.url"
              class="link"  
              target="_blank"
            >
              {{ details.application_protocols.name }}
            </a>
          </div>
        </li>

      </ul>

      <!-- If PrEST product -->
      <div v-if="typeof details.applications == 'string'" class="large-9 applications-aprest">
        <span>{{ details.applications }}</span>
        <div class="margin-top-20">
          <a
            v-if="details.application_protocols"
            :href="details.application_protocols.url"
            class="link"  
            target="_blank"
          >
            {{ details.application_protocols.name }}
          </a>
        </div>
      </div>
    </div>
    <!-- End Applications -->
    
    <!-- Validations in Applications -->
    <!-- <hr v-if="Array.isArray(details.applications)"> -->
    <div v-if="Array.isArray(details.applications)" class="product-details-section">
      <h2 class="large-3">Validation</h2>
      <ul v-if="Array.isArray(details.applications)" class="validations large-9">

        <li 
          v-for="application, applicationsKey in details.applications"
          :key="applicationsKey"
          class="margin-top-20"
        >
          <span class="key-value">{{ application.name }}&nbsp;({{ application.type }})</span>

          <span v-for="validation, validationKey in application.validations" :key="validationKey" class="block">
            <span>{{ validation.description }}&nbsp;</span>

            <a 
              v-for="link, linkKey in validation.link"
              :href="link.url"
              :key="linkKey"
              target="_blank"
              class="link"
            >
              {{ link.name }}
            </a>
          </span>
        </li>

      </ul>

      <!-- If PrEST product -->
      <div v-if="typeof details.applications == 'string'" class="large-9 applications-aprest">
        <span>{{ details.validations }}</span>
      </div>

    </div>
    <!-- End Validations -->
    
    <!-- Target information -->
    <!-- <hr v-if="details.target"> -->
    <div v-if="details.target" class="product-details-section">
      <h2 class="large-3">Target information</h2>
      <ul class="target-information large-9">

        <li v-if="details.target.protein_name">
          <span>
            <span class="key-value">Protein name:&nbsp;</span>
            <span>{{ details.target.protein_name }}</span>
          </span>
        </li>
        <li v-if="details.target.gene_name">
          <span>
            <span class="key-value">Gene name:&nbsp;</span>
            <span>{{ details.target.gene_name }}</span>
          </span>
        </li>
        <li v-if="details.target.alternative_gene_names">
          <span class="key-value">Alternative gene names:&nbsp;</span>
          <span v-for="(name, index) in details.target.alternative_gene_names" :key="index">
            <span>{{ name }}</span>
            <span v-show="index != details.target.alternative_gene_names.length -1">,&nbsp;</span>
          </span>
        </li>
        <li v-for="item, index in details.target.databases" :key="index">
          <span class="key-value">{{ item.type }}:&nbsp;</span>
          <a :href="item.link?.url" target="_blank" class="link">
            {{ item.id }}
          </a>
        </li>

      </ul>
    </div>
    <!-- End Target information -->
    
    <!-- Shipping and storage -->
    <!-- <hr v-if="details.shipping || details.storage"> -->
    <div v-if="details.shipping || details.storage" class="product-details-section">
      <h2 class="large-3">Shipping and storage</h2>
      <ul class="shipping large-9">

        <li>
          <span v-if="details.shipping" class="block">
            <span class="key-value">Shipping:&nbsp;</span>
            <span>{{ details.shipping }}</span>
          </span>
          <span v-if="details.storage" class="block">
            <span class="key-value">Storage:&nbsp;</span>
            <span>{{ details.storage }}</span>
          </span>
        </li>

      </ul>
    </div>
    <!-- End Shipping and storage -->
    
    <!-- References -->
    <!-- <hr> -->
    <div class="references">
      <h2>References {{ details.references?.length ? `(${details.references.length})` : '' }}</h2>
      <div v-if="details.references">
        <ul>
          <li 
            v-for="reference, referenceKey in referencesList" :key="referenceKey"
          >
            <span>
              <span v-if="reference.title" class="title block">{{ reference.title }}</span>
              <span v-if="reference.description" class="block margin-bottom-15 ref-description">{{ reference.description }}</span>
              <div v-if="reference.link">
                <a :href="reference.link.url" target="_blank" class="block margin-bottom-15 link">
                  {{ reference.link.name }}
                </a>
              </div>
              <span v-if="reference.author" class="block margin-bottom-15 ref-authors">{{ reference.author }}</span>
              <span v-if="reference.source" class="block ref-source">{{ reference.source }}</span>
              <span v-if="reference.publication_date" class="block margin-bottom-15 ref-date">{{ reference.publication_date }}</span>
              <div v-if="reference.pubmed">
                <a :href="reference.pubmed.url" target="_blank" class="link pubmed-id">
                  {{ reference.pubmed.name }}
                </a>
              </div>
              <div v-if="reference.doi">
                <a :href="reference.doi.url" target="_blank" class="link doi">
                  {{ reference.doi.name }}
                </a>
              </div>
            </span>
            <hr>
          </li>

        </ul>
        <button
          v-if="details.references.length > 2"
          class="show-more-references-button button"
          @click="referencesShowAll = !referencesShowAll"
        >
          <PlusCircleIcon v-show="!referencesShowAll"/>
          {{ !referencesShowAll ? 'Show more references' : 'Show less references' }}
        </button>
      </div>

      <div class="highlight-container">
        <span class="title block">Did we miss your publication?</span>
        <span v-if="details.product_number" class="highlight-container-text">
          Have you published using {{ details.product_number }}? Please let us know and we will be happy to include your reference on this page.
        </span>
  
        <a 
          href="https://www.atlasantibodies.com/customer-support/submit-your-reference/"
          class="button submit-reference-button"
        >
          <PencilIcon/> Submit reference
        </a>
      </div>

    </div>
    <!-- End References -->
    
    <!-- Researcher Contributions -->
    <div 
    v-if="packaging.current_lot"
    class="researcher-contributions"
    >
      <hr>
      <h2>Researcher Contributions</h2>
      <ul v-if="details.researcher_contributions">

        <li v-for="research, key in details.researcher_contributions" :key="key">
          <span class="title block">{{ research.subject }}</span>
          <span class="research-submitted-by block margin-top-10">{{ research.submitted_by }}</span>
          <span class="research-submitted-by block margin-top-10">{{ research.submission_date }}</span>
          <span class="research-description block margin-top-10 margin-bottom-15">{{ research.description }}</span>
          <span v-html="research.content"></span>
        </li>

      </ul>
    </div>

    <div 
      v-if="packaging.current_lot"
      class="highlight-container explorer-program-container"
    >
      <span class="title block">Join the Explorer Program</span>
      <span class="highlight-container-text">
        Are you using our products in an application or species we have not yet tested? Why not participate in the Explorer Program,
        and we will show your contribution here. If you would like to share your results with us,
        the Explorer Program offers a 25µl vial free of charge with your next order.
      </span>
      <a class="block secondary margin-top-10" href="https://www.atlasantibodies.com/customer-support/explorer-program/">Read more...</a>
    </div>
    <!-- End Researcher Contributions -->
  </div>
</template>

<script>
import { ref, computed, inject } from 'vue';
import ClipboardIcon from '@/components/icons/ClipboardIcon.vue';
import PlusCircleIcon from '@/components/icons/PlusCircleIcon.vue';
import PencilIcon from '@/components/icons/PencilIcon.vue';
import MolBooleanLogoIcon from '@/components/icons/MolBooleanLogoIcon.vue';

export default {
  props: {
    productId: {
      required: false,
      type: Number
    },
    tags: {
      required: false,
      type: Object,
    },
    packaging: {
      required: false,
      type: Object,
    },
    categoryId: { // MolBoolean = 615
      required: false,
    },
  },
  components: {
    ClipboardIcon,
    PlusCircleIcon,
    PencilIcon,
    MolBooleanLogoIcon
  },
  setup(props) {
    const { $translate } = inject('i18n');
    const referencesShowAll = ref(false);
    let selectedCurrentLot = ref(null);
    const details = ref({});


    const molBooleanKitText = computed(() => {
      const translated = $translate('C_MOLBOOLEAN_KIT');
      return translated && translated.trim() !== '' ? translated : null;
    });

    const fetchExtendedProductFieldsData = async () => {
      fetch('/surface/1/extended-product-data-field', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: props.productId,
          fields: [{ id: 1 }]
        })
      })
      .then(response => response.json())
      .then(data => {
          const jsonString = data[props.productId][1].value;
          const parsedJsonDetails = JSON.parse(jsonString);

          if (parsedJsonDetails) {
            details.value = parsedJsonDetails;
          } else {
            console.error('No details to display')
          }
      })
      .catch(error => {
        console.error('No details to display', error)
      });
    };
    fetchExtendedProductFieldsData();

    const unescapeHtml = (html) => {
      var tempDivElement = document.createElement("div");
      tempDivElement.innerHTML = html;
      return tempDivElement.textContent || tempDivElement.innerText || "";
    }

    const copySequenceToClipboard = (sequence) => {
      navigator.clipboard.writeText(sequence);
    };

    const referencesList = computed(() => {
      // Show 2 references from start
      if (referencesShowAll.value || details.value.references.length <= 2) {
        return details.value.references;
      } else {
        return details.value.references.slice(0, 2);
      }
    });

    const currentLotSimplified = computed(() => {
      let lot = null;
      let currentLots = props.packaging?.current_lot?.map(item => {
      let key = Object.keys(item)[0];
      let volume = key.replace('ul', 'µl');
        return {
            volume: volume,
            current_batch: item[key].current_batch,
            concentration: item[key].concentration
        };
      });

      currentLots?.forEach(item => {
        if (item.volume == selectedCurrentLot.value) {
          lot = item;
        }
      })

      return lot;
    });

    let variantSelectors = document.querySelectorAll('[data-current-lot-name]');

    variantSelectors.forEach(variant => {
      variant.addEventListener('click', () => {
        selectedCurrentLot.value = variant.getAttribute('data-current-lot-name');
      })
      
      if (variant.getAttribute('checked')) {
        selectedCurrentLot.value = variant.getAttribute('data-current-lot-name');
      }
    });
    
    const scrollToSection = (section) => {
      let referencesSection = document.querySelector(section);
      referencesSection.scrollIntoView({behavior: 'smooth'});
      setTimeout(() => {
        window.scroll({
          top: window.scrollY - 85,
          behavior: 'smooth'
        });
      }, 1000);
    };

    const referencesButton = document.querySelector('[data-scroll-to-references]');
    referencesButton?.addEventListener('click', () => {
      scrollToSection('.references');
    });

    const researchButton = document.querySelector('[data-scroll-to-research]');
    researchButton?.addEventListener('click', () => {
      scrollToSection('.researcher-contributions');
    });

    return {
      copySequenceToClipboard,
      referencesShowAll,
      referencesList,
      currentLotSimplified,
      selectedCurrentLot,
      unescapeHtml,
      details,
      molBooleanKitText
    }
  }
}
</script>